import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Typography,
  Box,
  Paper,
  Modal,
  Button,
} from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../../services/api/resources";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { Skeleton } from "@mui/material";
import Logo from "../../../assets/images/svg/Logo.svg";
import { useSelector } from "react-redux";
import tableClasses from "../../../components/common/AnalysisTable/AnalysisTable.module.css";

const GstAccordionTable = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const buyer_token = loginData?.user?.token;

  const [vendors, setVendors] = useState([]);
  // const [vendors, setVendors] = useState(vendor);
  const [openRows, setOpenRows] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedSummary, setSelectedSummary] = useState("");

  // useEffect(() => {
  //     setLoading(true)
  //     axios.post(API_ENDPOINT.gst_filing_details).then(res => {
  //         setVendors(res.data || []);
  //         setLoading(false)
  //     });
  // }, []);

  const fetchVendorGSTFiling = async () => {
    try {
      const response = await axios.post(API_ENDPOINT.gst_filing_details, {
        buyer_token: buyer_token,
      });
      setVendors(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  useEffect(() => {
    fetchVendorGSTFiling();
  }, []);

  const toggleRow = (index) => {
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllRows = () => {
    const anyOpen = Object.values(openRows).some((isOpen) => isOpen);
    const updated = {};
    vendors.forEach((_, idx) => {
      updated[idx] = !anyOpen;
    });
    setOpenRows(updated);
  };

  const extractReturns = (filing_status, type) => {
    return (filing_status?.result || []).flatMap(
      (period) =>
        (period.eFiledlist || [])
          .filter((r) => r.rtntype === type)
          .map((r) => {
            const hasValidDueDate =
              r.dueDt && /^\d{4}-\d{2}-\d{2}$/.test(r.dueDt);
            const hasValidDOF = r.dof && /^\d{2}-\d{2}-\d{4}$/.test(r.dof);

            let earlyDays = null;

            if (hasValidDueDate && hasValidDOF && r.isDelay === false) {
              const dateOfFiling = new Date(
                r.dof.split("-").reverse().join("-")
              );
              const dueDate = new Date(r.dueDt);

              if (!isNaN(dateOfFiling) && !isNaN(dueDate)) {
                const diffTime = dueDate - dateOfFiling;
                earlyDays =
                  diffTime > 0
                    ? Math.floor(diffTime / (1000 * 60 * 60 * 24))
                    : 0;
              }
            }

            return {
              finYear: period.financialYear,
              taxPeriod: formatTaxPeriod(r.retPrd),
              dateOfFiling: r.dof,
              status: r.status,
              delayDays: r.delayDays,
              isDelay: r.isDelay,
              earlyFilingDays: earlyDays ?? "N/A",
            };
          })

      // .map(r => ({
      //     // finYear: period.financialYear,
      //     // taxPeriod: formatTaxPeriod(r.retPrd),
      //     // dateOfFiling: r.dof,
      //     // status: r.status,
      //     // mof: r.mof,
      //     // delayDays: r.delayDays,
      //     // isDelay: r.isDelay
      // }))
    );
  };

  const formatTaxPeriod = (retPrd) => {
    const monthMap = {
      "01": "January",
      "02": "February",
      "03": "March",
      "04": "April",
      "05": "May",
      "06": "June",
      "07": "July",
      "08": "August",
      "09": "September",
      10: "October",
      11: "November",
      12: "December",
    };
    const month = retPrd?.slice(0, 2);
    return monthMap[month] || retPrd;
  };

  const getSummaryText = (vendor) => {
    const gstr3b = extractReturns(vendor.filing_status, "GSTR3B");
    if (!gstr3b.length) return "Summary: Nil";

    const finYears = new Set();
    const delayDetails = [];

    gstr3b.forEach((f) => {
      finYears.add(f.finYear);
      if (f.isDelay)
        delayDetails.push({ month: f.taxPeriod, days: f.delayDays || 0 });
    });

    const delayMonths = delayDetails.length;
    const delayCount = delayDetails.reduce(
      (acc, d) => acc + (d.days > 0 ? 1 : 0),
      0
    );
    const score = (() => {
      const successRatio = ((gstr3b.length - delayCount) / gstr3b.length) * 100;
      if (successRatio >= 90) return "Excellent";
      if (successRatio >= 70) return "Good";
      return "Average";
    })();

    const monthsLine = delayDetails
      .map((d, i) => `the month of ${d.month} is ${d.days} day(s)`)
      .join(", and ");

    return `This entity, <strong>${
      vendor.company_name
    } </strong>, has filed GST for the financial year(s) ${Array.from(
      finYears
    ).join(
      ", "
    )} with ${delayCount} delay(s) over a period of ${delayMonths} month(s). ${monthsLine}. A total of ${delayCount} delay(s) were found in this report. Therefore, their score is ${score}.

        Despite the delays, it is important to note the overall compliance behavior, including early and on-time submissions, which also contribute to the entity's compliance performance. Based on the filing pattern and weighted scoring methodology that accounts for timely and proactive filings, Teckost has achieved a compliance score of ${score}
        `;
  };

  return (
    <>
      <HeaderTitle titleText={"Supplier GST Filing Analysis and Compliance"} />
      <TableContainer component={Paper} className={tableClasses.tableSurface}>
        <div className={tableClasses.tableScroll}>
          <Table className={tableClasses.tableRoot}>
          <TableHead>
            <TableRow className={tableClasses.headerRow}>
              <TableCell
                onClick={toggleAllRows}
                sx={{ cursor: "pointer" }}
                className={`${tableClasses.headerCell} ${tableClasses.controlCell}`}
              >
                <ArrowUpIcon />
                <ArrowDownIcon />
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Company Name
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                GSTIN
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                PAN
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Defaulter
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Delay in Filing
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Delay %
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Early Filing %
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Score
              </TableCell>
              <TableCell className={tableClasses.headerCell}>
                Summary
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className={tableClasses.bodyRow}>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <TableCell key={j} className={tableClasses.bodyCell}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : vendors.map((vendor, index) => {
                  const gstr3b = extractReturns(vendor.filing_status, "GSTR3B");
                  const isOpen = openRows[index];

                  const totalFiled = gstr3b.length;
                  const delayCount = gstr3b.filter((r) => r.isDelay).length;
                  const delayedFiled = gstr3b.filter(
                    (row) => row.isDelay === true
                  ).length;
                  const successFiled = totalFiled - delayedFiled;

                  const delayPercent = totalFiled
                    ? Math.round((delayCount / totalFiled) * 100)
                    : 0;
                  const earlyPercent =
                    totalFiled > 0
                      ? Math.round((successFiled / totalFiled) * 100)
                      : 0;

                  return (
                    <React.Fragment key={index}>
                      <TableRow className={tableClasses.bodyRow}>
                        <TableCell
                          className={`${tableClasses.bodyCell} ${tableClasses.controlCell}`}
                        >
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(index)}
                            className={tableClasses.iconButton}
                          >
                            {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell className={tableClasses.bodyCell}>
                          {vendor.company_name}
                        </TableCell>
                        <TableCell className={tableClasses.bodyCell}>
                          {vendor.filing_status?.gstin || "N/A"}
                        </TableCell>
                        <TableCell className={tableClasses.bodyCell}>
                          {vendor.pan_number}
                        </TableCell>
                        <TableCell className={tableClasses.bodyCell}>
                          {vendor.filing_status?.complianceStatus
                            ?.isDefaulter === true
                            ? "Yes"
                            : vendor.filing_status?.complianceStatus
                                ?.isDefaulter === false
                            ? "No"
                            : "N/A"}
                        </TableCell>

                        <TableCell className={tableClasses.bodyCell}>
                          {vendor.filing_status?.complianceStatus
                            ?.isAnyDelay === true
                            ? "Yes"
                            : vendor.filing_status?.complianceStatus
                                ?.isAnyDelay === false
                            ? "No"
                            : "N/A"}
                        </TableCell>

                        <TableCell className={tableClasses.bodyCell}>
                          {delayPercent}%
                        </TableCell>

                        <TableCell className={tableClasses.bodyCell}>
                          {earlyPercent}%
                        </TableCell>

                        <TableCell className={tableClasses.bodyCell}>
                          {(() => {
                            const gstr3b = extractReturns(
                              vendor.filing_status,
                              "GSTR3B"
                            );
                            if (!gstr3b || gstr3b.length === 0) return "-";
                            const delayed = gstr3b.filter(
                              (r) => r.isDelay === true
                            );
                            const percentage =
                              gstr3b.length === 0
                                ? 0
                                : (delayed.length / gstr3b.length) * 100;
                            if (percentage <= 10) return "Excellent";
                            if (percentage <= 30) return "Good";
                            if (percentage <= 60) return "Average";
                            return "Poor";
                          })()}
                        </TableCell>

                        <TableCell className={tableClasses.bodyCell}>
                          <Button
                            variant="contained"
                            className={tableClasses.summaryButton}
                            onClick={() => {
                              setSelectedSummary(getSummaryText(vendor));
                              setModalOpen(true);
                            }}
                          >
                            View Summary
                          </Button>
                        </TableCell>
                      </TableRow>

                      {isOpen && (
                        <TableRow className={tableClasses.bodyRow}>
                          <TableCell
                            colSpan={10}
                            sx={{ p: 0, borderBottom: 0 }}
                          >
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box className={tableClasses.nestedWrap}>
                                <TableContainer
                                  component={Paper}
                                  className={tableClasses.tableSurface}
                                >
                                  <div className={tableClasses.tableScroll}>
                                    <Table
                                      size="large"
                                      className={`${tableClasses.tableRoot} ${tableClasses.tableRootCompact}`}
                                    >
                                      <TableHead>
                                        <TableRow
                                          className={tableClasses.headerRow}
                                        >
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Financial Year
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Month
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Date of Filing
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Delayed Filing
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Delay in Days
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Early Filing Days
                                          </TableCell>
                                          <TableCell
                                            className={tableClasses.headerCell}
                                          >
                                            Status
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {gstr3b.length > 0 ? (
                                          gstr3b.map((row, i) => (
                                            <TableRow
                                              key={i}
                                              className={tableClasses.bodyRow}
                                            >
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.finYear}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.taxPeriod}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.dateOfFiling}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.isDelay ? "Yes" : "No"}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.delayDays}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.earlyFilingDays ?? "N/A"}
                                              </TableCell>
                                              <TableCell
                                                className={tableClasses.bodyCell}
                                              >
                                                {row.status}
                                              </TableCell>
                                            </TableRow>
                                          ))
                                        ) : (
                                          <TableRow>
                                            <TableCell
                                              colSpan={7}
                                              align="center"
                                              className={tableClasses.emptyState}
                                            >
                                              No GSTR3B filings found
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableContainer>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
          </TableBody>
          </Table>
        </div>

        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box className={tableClasses.modalPaper}>
            <div className={tableClasses.modalHeader}>
              <Typography
                variant="h6"
                className={tableClasses.modalTitle}
              >
                Summary of the Report
              </Typography>
              <img src={Logo} width={"80"} />
            </div>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{ __html: selectedSummary }}
              className={tableClasses.modalBody}
            />

            <Box className={tableClasses.modalActions}>
              <Button
                onClick={() => setModalOpen(false)}
                variant="contained"
                className={tableClasses.closeButton}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      </TableContainer>
    </>
  );
};

export default GstAccordionTable;
